import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState } from "react";
import { Play, X } from "lucide-react";
import { useLang } from "./LangContext";

function VideoModal({ project, onClose, clientLabel }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-modal"
    >
      <button
        onClick={onClose}
        data-testid="video-modal-close"
        className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
      >
        <X className="w-5 h-5 text-white" />
      </button>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-5xl"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-white mb-3 text-sm" style={{ fontFamily: 'Outfit' }}>
          {project.client && <><span className="text-[#73B5E2] font-medium">{project.client}</span> · </>}
          {project.title}
        </p>
        <video autoPlay controls playsInline className="w-full rounded-xl shadow-2xl" data-testid="video-player">
          <source src={project.video} type="video/mp4" />
        </video>
      </motion.div>
    </motion.div>
  );
}

export default function Portfolio() {
  const { t } = useLang();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [activeFilter, setActiveFilter] = useState("tous");
  const [activeVideo, setActiveVideo] = useState(null);

  const projects = [
    { url: "/patrickimmo_payerne.jpg", categoryKey: "immobilier", title: t.portfolio.projects.payerne, client: "Patrick Immobilier", video: "/payerne.mp4" },
    { url: "/patrickimmo_arbaz.jpg", categoryKey: "immobilier", title: t.portfolio.projects.arbaz, client: "Patrick Immobilier", video: "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/6vx4i562_patrickimmo_arbaz_16-9_18.01.26.mp4" },
    { url: "/fromagerie_marsens.jpg", categoryKey: "charpente", title: t.portfolio.projects.fromagerie, client: "Gex et Dorthe" },
    { url: "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/vudiz985__DSC1271-HDR-Enhanced-NR-Edit.jpg", categoryKey: "charpente", title: t.portfolio.projects.pont, client: "Gex et Dorthe" },
    { url: "/chantier_bulle.jpg", categoryKey: "chantier", title: t.portfolio.projects.chantier, video: "https://customer-assets.emergentagent.com/job_prodimmo-media/artifacts/jqq6ukm1_chantier_random_1_chf3_prob4%202.MP4" },
    { url: "/prodimmo_insta_thumb.jpg", categoryKey: "immobilier", title: t.portfolio.projects.insta, client: "Patrick Immobilier", video: "/prodimmo_insta.mp4" },
  ];

  const filterKeys = ["tous", "immobilier", "charpente", "chantier"];

  const filtered = activeFilter === "tous"
    ? projects
    : projects.filter((p) => p.categoryKey === activeFilter);

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-[#73B5E2] font-medium text-sm uppercase tracking-widest">
            {t.portfolio.label}
          </span>
          <h2
            data-testid="portfolio-title"
            className="mt-3 text-3xl sm:text-4xl font-bold text-slate-900"
            style={{ fontFamily: 'Outfit' }}
          >
            {t.portfolio.title}
          </h2>
        </motion.div>

        <div className="flex justify-center gap-3 mb-12 flex-wrap">
          {filterKeys.map((key) => (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => setActiveFilter(key)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeFilter === key
                  ? "bg-[#73B5E2] text-white shadow-md"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {t.portfolio.filters[key]}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((project, i) => (
            <motion.div
              key={project.categoryKey + i}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="portfolio-item relative overflow-hidden rounded-2xl aspect-[4/3] group cursor-pointer"
              data-testid={`portfolio-item-${i}`}
              onClick={() => project.video && setActiveVideo(project)}
            >
              <img src={project.url} alt={project.title} className="w-full h-full object-cover transition-transform duration-700" />
              {project.video && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Play className="w-6 h-6 text-[#73B5E2] ml-1" fill="#73B5E2" />
                  </div>
                </div>
              )}
              <div className="portfolio-overlay absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 transition-opacity duration-300 flex flex-col justify-end p-6">
                <span className="text-[#73B5E2] text-xs font-medium uppercase tracking-wider">
                  {t.portfolio.filters[project.categoryKey]}
                </span>
                <h3 className="text-white text-xl font-semibold mt-1" style={{ fontFamily: 'Outfit' }}>
                  {project.title}
                </h3>
                {project.client && (
                  <p className="text-white/70 text-sm mt-1">{t.portfolio.client} : {project.client}</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeVideo && <VideoModal project={activeVideo} onClose={() => setActiveVideo(null)} />}
      </AnimatePresence>
    </section>
  );
}
